import * as THREE from 'three'
import { getLoggerManager } from '@/shared/logging'
import { fnv32a } from '@/shared/utils/hash'
import { attributeLocations } from './utils'

const log = getLoggerManager().getLogger('pointcloud')
/** 缓存着色器对象 */
interface CachedShader {
  program: WebGLProgram | null
  vs: WebGLShader | null
  fs: WebGLShader | null
  attributeLocations: Record<string, number>
  uniformLocations: Record<string, WebGLUniformLocation | null>
  uniforms: Record<string, any>
  uniformBlocks: Record<string, any>
}

/**
 * 轻量 WebGL2 shader 封装，支持按源码 hash 缓存与 uniform 查询。
 */
export class Shader {
  /** WebGL2 上下文 */
  gl: WebGL2RenderingContext
  /** shader 名称，调试/报错使用 */
  name: string
  /** 当前顶点 shader 源码 */
  vsSource: string
  /** 当前片段 shader 源码 */
  fsSource: string
  /** 按源码 hash 缓存的已编译程序 */
  cache: Map<number, CachedShader>
  /** 当前顶点 WebGLShader */
  vs: WebGLShader | null
  /** 当前片段 WebGLShader */
  fs: WebGLShader | null
  /** 当前链接成功的 WebGLProgram */
  program: WebGLProgram | null
  /** uniform 名到 location 的映射 */
  uniformLocations: Record<string, WebGLUniformLocation | null>
  /** 顶点属性名到 location 的映射 */
  attributeLocations: Record<string, number>
  /** uniform block 名到 index 的映射 */
  uniformBlockIndices: Record<string, number>
  /** uniform block 元信息 */
  uniformBlocks: Record<string, any>
  /** uniform 元信息 */
  uniforms: Record<string, any>

  constructor(gl: WebGL2RenderingContext, name: string, vsSource: string, fsSource: string) {
    this.gl = gl
    this.name = name
    this.vsSource = vsSource
    this.fsSource = fsSource

    this.cache = new Map()

    this.vs = null
    this.fs = null
    this.program = null

    this.uniformLocations = {}
    this.attributeLocations = {}
    this.uniformBlockIndices = {}
    this.uniformBlocks = {}
    this.uniforms = {}

    this.update(vsSource, fsSource)
  }

  /**
   * 替换 shader 源码并重新链接程序。
   *
   * @param vsSource - 新的顶点 shader 源码
   * @param fsSource - 新的片段 shader 源码
   */
  update(vsSource: string, fsSource: string): void {
    this.vsSource = vsSource
    this.fsSource = fsSource

    this.linkProgram()
  }

  /**
   * 编译一个 GLSL shader，失败抛出带行号的源码信息。
   *
   * @param shader - 目标 WebGLShader 对象
   * @param source - GLSL 源码
   */
  compileShader(shader: WebGLShader, source: string): void {
    const gl = this.gl

    gl.shaderSource(shader, source)

    gl.compileShader(shader)

    const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS)
    if (!success) {
      const info = gl.getShaderInfoLog(shader)
      const numberedSource = source
        .split('\n')
        .map((a, i) => `${i + 1}`.padEnd(5) + a)
        .join('\n')
      throw `could not compile shader ${this.name}: ${info}, \n${numberedSource}`
    }
  }

  /** 链接当前 vs/fs 源码为 WebGLProgram，自动复用 cache。 */
  linkProgram(): void {
    const tStart = performance.now()

    const gl = this.gl

    this.uniformLocations = {}
    this.attributeLocations = {}
    this.uniforms = {}

    // biome-ignore lint/correctness/useHookAtTopLevel: gl.useProgram 是 WebGL API，非 React hook
    gl.useProgram(null)

    const key = fnv32a(this.vsSource + this.fsSource)
    const cached = this.cache.get(key)
    if (cached) {
      this.program = cached.program
      this.vs = cached.vs
      this.fs = cached.fs
      this.attributeLocations = cached.attributeLocations
      this.uniformLocations = cached.uniformLocations
      this.uniformBlocks = cached.uniformBlocks
      this.uniforms = cached.uniforms

      return
    } else {
      this.vs = gl.createShader(gl.VERTEX_SHADER)
      this.fs = gl.createShader(gl.FRAGMENT_SHADER)
      this.program = gl.createProgram()
      if (!this.program) {
        throw new Error('Failed to create program')
      }

      for (const name of Object.keys(attributeLocations)) {
        const location = attributeLocations[name].location
        const glslName = attributeLocations[name].name
        gl.bindAttribLocation(this.program, location, glslName)
      }

      if (this.vs && this.fs && this.program) {
        this.compileShader(this.vs, this.vsSource)
        this.compileShader(this.fs, this.fsSource)

        gl.attachShader(this.program, this.vs)
        gl.attachShader(this.program, this.fs)

        gl.linkProgram(this.program)

        gl.detachShader(this.program, this.vs)
        gl.detachShader(this.program, this.fs)
      }

      if (!this.program) {
        return
      }

      const success = gl.getProgramParameter(this.program, gl.LINK_STATUS)
      if (!success) {
        const info = gl.getProgramInfoLog(this.program)
        throw `could not link program ${this.name}: ${info}`
      }

      {
        // attribute locations
        const numAttributes = gl.getProgramParameter(this.program, gl.ACTIVE_ATTRIBUTES)

        for (let i = 0; i < numAttributes; i++) {
          const attribute = gl.getActiveAttrib(this.program, i)!

          const location = gl.getAttribLocation(this.program, attribute.name)

          this.attributeLocations[attribute.name] = location
        }
      }

      {
        // uniform locations
        const numUniforms = gl.getProgramParameter(this.program, gl.ACTIVE_UNIFORMS)

        for (let i = 0; i < numUniforms; i++) {
          const uniform = gl.getActiveUniform(this.program, i)!

          const location = gl.getUniformLocation(this.program, uniform.name)

          this.uniformLocations[uniform.name] = location
          this.uniforms[uniform.name] = {
            location: location,
            value: null,
          }
        }
      }

      // uniform blocks
      if (gl instanceof WebGL2RenderingContext) {
        const numBlocks = gl.getProgramParameter(this.program, gl.ACTIVE_UNIFORM_BLOCKS)

        for (let i = 0; i < numBlocks; i++) {
          const blockName = gl.getActiveUniformBlockName(this.program, i)!

          const blockIndex = gl.getUniformBlockIndex(this.program, blockName)

          this.uniformBlockIndices[blockName] = blockIndex

          gl.uniformBlockBinding(this.program, blockIndex, blockIndex)
          const dataSize = gl.getActiveUniformBlockParameter(
            this.program,
            blockIndex,
            gl.UNIFORM_BLOCK_DATA_SIZE
          )

          const uBuffer = gl.createBuffer()
          gl.bindBuffer(gl.UNIFORM_BUFFER, uBuffer)
          gl.bufferData(gl.UNIFORM_BUFFER, dataSize, gl.DYNAMIC_READ)

          gl.bindBufferBase(gl.UNIFORM_BUFFER, blockIndex, uBuffer)

          gl.bindBuffer(gl.UNIFORM_BUFFER, null)

          this.uniformBlocks[blockName] = {
            name: blockName,
            index: blockIndex,
            dataSize: dataSize,
            buffer: uBuffer,
          }
        }
      }

      const cached: CachedShader = {
        program: this.program,
        vs: this.vs,
        fs: this.fs,
        attributeLocations: this.attributeLocations,
        uniformLocations: this.uniformLocations,
        uniforms: this.uniforms,
        uniformBlocks: this.uniformBlocks,
      }

      this.cache.set(key, cached)
    }

    const tEnd = performance.now()
    const duration = tEnd - tStart

    log.debug(`shader compile duration: ${duration.toFixed(3)}`)
  }

  /**
   * 以 mat4 形式上传 uniform。
   *
   * @param name - uniform 名
   * @param value - 4x4 矩阵
   */
  setUniformMatrix4(name: string, value: THREE.Matrix4): void {
    const gl = this.gl
    const location = this.uniformLocations[name]

    if (location == null) {
      return
    }

    const tmp = new Float32Array(value.elements)
    gl.uniformMatrix4fv(location, false, tmp)
  }

  /**
   * 以 float 形式上传 uniform，值未变化时跳过。
   *
   * @param name - uniform 名
   * @param value - 浮点值
   */
  setUniform1f(name: string, value: number): void {
    const gl = this.gl
    const uniform = this.uniforms[name]

    if (uniform === undefined) {
      return
    }

    if (uniform.value === value) {
      return
    }

    uniform.value = value

    gl.uniform1f(uniform.location, value)
  }

  /**
   * 以 int 形式上传布尔 uniform，值未变化时跳过。
   *
   * @param name - uniform 名
   * @param value - 布尔值
   */
  setUniformBoolean(name: string, value: boolean): void {
    const gl = this.gl
    const uniform = this.uniforms[name]

    if (uniform === undefined) {
      return
    }

    if (uniform.value === value) {
      return
    }

    uniform.value = value

    gl.uniform1i(uniform.location, value ? 1 : 0)
  }

  /**
   * 以 int 形式上传纹理采样单元索引。
   *
   * @param name - uniform 名
   * @param value - 采样单元索引，如 0/1/2
   */
  setUniformTexture(name: string, value: number): void {
    const gl = this.gl
    const location = this.uniformLocations[name]

    if (location == null) {
      return
    }

    gl.uniform1i(location, value)
  }

  /**
   * 以 vec2 形式上传 uniform。
   *
   * @param name - uniform 名
   * @param value - 长度 2 的浮点数组
   */
  setUniform2f(name: string, value: [number, number]): void {
    const gl = this.gl
    const location = this.uniformLocations[name]

    if (location == null) {
      return
    }

    gl.uniform2f(location, value[0], value[1])
  }

  /**
   * 以 vec3 形式上传 uniform。
   *
   * @param name - uniform 名
   * @param value - 长度 3 的浮点数组
   */
  setUniform3f(name: string, value: [number, number, number]): void {
    const gl = this.gl
    const location = this.uniformLocations[name]

    if (location == null) {
      return
    }

    gl.uniform3f(location, value[0], value[1], value[2])
  }

  /**
   * 按值类型自动分发到具体 setUniformXX 方法。
   *
   * @param name - uniform 名
   * @param value - 待上传值，支持 Matrix4/number/boolean/数组
   */
  setUniform(name: string, value: any): void {
    if (value.constructor === THREE.Matrix4) {
      this.setUniformMatrix4(name, value)
    } else if (typeof value === 'number') {
      this.setUniform1f(name, value)
    } else if (typeof value === 'boolean') {
      this.setUniformBoolean(name, value)
    } else if (Array.isArray(value)) {
      if (value.length === 2) {
        this.setUniform2f(name, value as [number, number])
      } else if (value.length === 3) {
        this.setUniform3f(name, value as [number, number, number])
      }
    } else {
      log.error(`unhandled uniform type: ${name}, ${String(value)}`)
    }
  }

  /**
   * 以 int 形式上传 uniform。
   *
   * @param name - uniform 名
   * @param value - 整数值
   */
  setUniform1i(name: string, value: number): void {
    const gl = this.gl
    const location = this.uniformLocations[name]

    if (location == null) {
      return
    }

    gl.uniform1i(location, value)
  }
}
